import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import { Card, Form, Input, Select, Button, Space, Typography, Row, Col, Modal, Table, Spin } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ImageUpload from "../../components/banners/ImageUpload";
import videoService from "../../services/videoService";
import masterService from "../../services/masterService";

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const AddCategory = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [showsSpinner] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isAd, setIsAd] = useState("N");

  // Dropdown data
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [videoShows, setVideoShows] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [seasonsOfSeries, setSeasonsOfSeries] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);

  // Form data
  const [showsList, setShowsList] = useState([]);
  const [showForm, setShowForm] = useState({
    show: null,
    show_type: null,
    season: null,
  });

  // Load dropdown data
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      setLoading(true);

      // Load all dropdown data in parallel
      const [langs, tgs, videoShowsRes, tvShowsRes] = await Promise.allSettled([
        masterService.getVideoLanguages({
          isAll: true,
          status: "Y",
        }),
        masterService.getTags(),
        masterService.getVideoShows(),
        masterService.getTvShows(),
      ]);
      // Set languages
      if (langs.status === "fulfilled" && langs.value?.data?.status) {
        const langPayload = langs.value.data?.data;
        const langList = Array.isArray(langPayload)
          ? langPayload
          : langPayload?.data || [];
        setLanguages(langList);
      }

      if (tgs.status === "fulfilled" && tgs.value?.data?.status) {
        const tagPayload = tgs.value.data?.data;
        const tagList = Array.isArray(tagPayload)
          ? tagPayload
          : tagPayload?.data || [];
        setTags(tagList);
      }

      // Set video shows
      if (
        videoShowsRes.status === "fulfilled" &&
        videoShowsRes.value.data?.status
      ) {
        setVideoShows(videoShowsRes.value.data.data || []);
      }

      // Set TV shows
      if (tvShowsRes.status === "fulfilled" && tvShowsRes.value.data?.status) {
        setTvShows(tvShowsRes.value.data.data || []);
      }
    } catch (error) {
      console.error("Error loading dropdown data:", error);
      notify.error({ message: "Failed to load form data" });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Validate multilingual: no duplicate langs, require name if language set
      const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
      if (new Set(langs).size !== langs.length) {
        notify.error({ message: "Duplicate languages in multilingual data" });
        setLoading(false);
        return;
      }
      for (let i = 0; i < multilingualData.length; i++) {
        const d = multilingualData[i];
        if (d.lang_id && (!d.vc_name || !d.vc_description)) {
          notify.error({ message: `Language entry #${i + 1}: Category name and description are required when language is selected` });
          setLoading(false);
          return;
        }
      }

      // serialize parent vc_keywords (tags -> csv)
      const parentKeywords = Array.isArray(values.vc_keywords)
        ? values.vc_keywords.map((s) => String(s).trim()).filter(Boolean).join(',')
        : (values.vc_keywords || '');
      // serialize multilingual vc_keywords
      const multilingualSerialized = (multilingualData || []).map((d) => ({
        ...d,
        vc_keywords: Array.isArray(d.vc_keywords)
          ? d.vc_keywords.map((s) => String(s).trim()).filter(Boolean).join(',')
          : (d.vc_keywords || ''),
      }));

      const formData = {
        ...values,
        vc_keywords: parentKeywords,
        shows_list: showsList,
        multilingual_data: multilingualSerialized,
      };

      console.log("Submitting category data:", formData);
      const response = await videoService.addVideoCategory(formData);
      console.log("API Response:", response);

      if (
        response.data &&
        response.data.status &&
        response.data.statusCode === 200
      ) {
        notify.success({
          message: response.data.message || "Category added successfully!"
        });
        form.resetFields();
        setShowsList([]);
        setIsAd("N");
        navigate("/categories/category");
      } else {
        notify.error({ message: response.data.message || "Failed to add category" });
      }
    } catch (error) {
      console.error("Error adding category:", error);
      notify.error({ message: "Failed to add category" });
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
    setShowsList([]);
    setIsAd("N");
  };

  // Handle go back
  const handleGoBack = () => {
    navigate(-1);
  };

  // Handle number input (only allow numbers)
  const handleNumberInput = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 8 && // backspace
      charCode !== 46 && // delete
      charCode !== 37 && // left arrow
      charCode !== 39 && // right arrow
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
    }
  };

  // Handle is_ad change
  const handleIsAdChange = (value) => {
    setIsAd(value);
  };

  // Handle add show modal
  const handleAddShow = () => {
    setVisible(true);
  };

  // Handle show type change
  const handleShowTypeChange = (value) => {
    setShowForm((prev) => ({
      ...prev,
      show_type: value,
      show: null,
      season: null,
    }));
    setSeasonsOfSeries([]);
  };

  // Handle show selection change
  const handleShowChange = async (value) => {
    setShowForm((prev) => ({ ...prev, show: value, season: null }));

    // If it's a series, get seasons
    if (showForm.show_type === "series" && value) {
      try {
        const show = JSON.parse(value);
        const response = await masterService.getAllSeasonsOfTvShow({
          tv_show_id: show.tv_show_id,
        });

        if (response.data && response.data.status) {
          setSeasonsOfSeries(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching seasons:", error);
        notify.error({ message: "Failed to fetch seasons" });
      }
    }
  };

  // Handle season change
  const handleSeasonChange = (value) => {
    setShowForm((prev) => ({ ...prev, season: value }));
  };

  // Handle submit show
  const handleSubmitShow = () => {
    if (!showForm.show) {
      notify.error({ message: "Please select a show" });
      return;
    }

    const showData = JSON.parse(showForm.show);
    const showId =
      showForm.show_type === "movie"
        ? showData.video_show_id
        : showData.tv_show_id;

    const showTitle =
      showForm.show_type === "movie"
        ? showData.video_show_title
        : showData.tv_show_name;

    let seasonId = null;
    let seasonTitle = null;
    if (showForm.season) {
      const season = JSON.parse(showForm.season);
      seasonId = season.season_id;
      seasonTitle = season.season_title;
    }

    // Check for duplicates
    const isDuplicate = showsList.some(
      (item) =>
        item.show_id === showId &&
        item.show_type === showForm.show_type &&
        item.season_id === seasonId
    );

    if (isDuplicate) {
      notify.error({ message: "This show is already added" });
      return;
    }

    // Add to shows list
    const newShow = {
      show_id: showId,
      show_title: showTitle,
      show_type: showForm.show_type,
      season_id: seasonId,
      season_title: seasonTitle,
    };

    setShowsList((prev) => [...prev, newShow]);
    setShowForm({ show: null, show_type: null, season: null });
    setSeasonsOfSeries([]);
    setVisible(false);
  };

  // Handle remove show
  const handleRemoveShow = (index) => {
    setShowsList((prev) => prev.filter((_, i) => i !== index));
  };

  // Shows table columns
  const showsColumns = [
    {
      title: "Order",
      key: "order",
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Show Title",
      key: "show_title",
      render: (text, record) => (
        <span>
          {record.show_title}
          {record.season_title ? ` - ${record.season_title}` : ""}
        </span>
      ),
    },
    {
      title: "Show Type",
      dataIndex: "show_type",
      key: "show_type",
      width: 120,
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (text, record, index) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveShow(index)}
          size="small"
        >
          Remove
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
    <>
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_ad: "N",
            home_page: "Y",
            movie_page: "Y",
            series_page: "Y",
            vc_is_active: "Y",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="Category Name"
                name="vc_name"
                rules={[
                  { required: true, message: "Please enter category name" },
                ]}
              >
                <Input placeholder="Enter category name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Order"
                name="order"
                rules={[
                  { required: true, message: "Please enter order" },
                  {
                    pattern: /^(0|[1-9]\d*)$/,
                    message: "Order must be a non-negative integer",
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="Enter order"
                  onKeyPress={handleNumberInput}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Category Description" name="vc_description">
            <TextArea rows={4} placeholder="Enter category description" />
          </Form.Item>

          <Form.Item label="Keywords" name="vc_keywords">
            <Select mode="tags" placeholder="Type a keyword and press Enter" tokenSeparators={[',']} open={false} />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label="Is Ad" name="is_ad">
                <Select onChange={handleIsAdChange}>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Status" name="vc_is_active">
                <Select>
                  <Option value="Y">Active</Option>
                  <Option value="N">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Ad-related fields */}
          {isAd === "Y" && (
            <>
              <Form.Item label="Ad URL" name="ad_url">
                <Input placeholder="Enter ad URL" />
              </Form.Item>

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <ImageUpload
                    label="Upload Card Image"
                    name="card_image"
                    width={300}
                    height={400}
                    imageType="video"
                    required={false}
                  />
                </Col>

                <Col span={12}>
                  <ImageUpload
                    label="Upload Banner Image"
                    name="banner_image"
                    width={1280}
                    height={720}
                    imageType="ads"
                    required={false}
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Page visibility options */}
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item label="Show on Home Page" name="home_page">
                <Select>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Show on Movie Page" name="movie_page">
                <Select>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Show on Series Page" name="series_page">
                <Select>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label="Choose Language" name="lang_id">
                <Select placeholder="Please select a language" allowClear>
                  {(Array.isArray(languages) ? languages : []).map(
                    (language) => (
                      <Option key={language.lang_id} value={language.lang_id}>
                        {language.lang_name}
                      </Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Tag" name="tag_id">
                <Select placeholder="Please select Tag">
                  {(Array.isArray(tags) ? tags : []).map((tag) => (
                    <Option key={tag.tag_id} value={tag.tag_id}>
                      {tag.tag_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Multilingual Data Section */}
          <Card style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Title level={4} style={{ margin: 0 }}>Multilingual Data</Title>
              <Button type="dashed" onClick={() => setMultilingualData([...multilingualData, { lang_id: null, vc_name: '', vc_description: '', vc_keywords: [] }])}>
                Add Language
              </Button>
            </div>
            {multilingualData.length === 0 && (
              <div style={{ textAlign: 'center', padding: 12, background: '#fafafa' }}>
                <Text type="secondary">No multilingual data added yet. Click "Add Language" to get started.</Text>
              </div>
            )}
            <div style={{ display: 'grid', gap: 12 }}>
              {multilingualData.map((data, index) => (
                <Card
                  key={`ml-${index}`}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong>
                        Language Entry #{index + 1}
                        {data.lang_id && ` - ${languages.find((l) => l.lang_id === data.lang_id)?.lang_name || 'Unknown Language'}`}
                      </Text>
                      <Button type="link" danger onClick={() => setMultilingualData(multilingualData.filter((_, i) => i !== index))}>
                        Remove
                      </Button>
                    </div>
                  }
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Form.Item label="Language" required style={{ marginBottom: 0 }}>
                      <Select
                        value={data.lang_id}
                        placeholder="Select language"
                        onChange={(value) => {
                          const arr = [...multilingualData];
                          arr[index].lang_id = value;
                          setMultilingualData(arr);
                        }}
                        showSearch
                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                      >
                        {(Array.isArray(languages) ? languages : []).map((lang) => (
                          <Option key={lang.lang_id} value={lang.lang_id}>
                            {lang.lang_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item label="Category Name" required style={{ marginBottom: 0 }}>
                      <Input
                        value={data.vc_name}
                        placeholder="Enter category name in this language"
                        onChange={(e) => {
                          const arr = [...multilingualData];
                          arr[index].vc_name = e.target.value;
                          setMultilingualData(arr);
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                    <Form.Item label="Description" required style={{ marginBottom: 0 }}>
                      <Input.TextArea
                        rows={3}
                        value={data.vc_description}
                        placeholder="Enter category description in this language"
                        onChange={(e) => {
                          const arr = [...multilingualData];
                          arr[index].vc_description = e.target.value;
                          setMultilingualData(arr);
                        }}
                      />
                    </Form.Item>
                    <Form.Item label="Keywords" style={{ marginBottom: 0 }}>
                      <Select
                        mode="tags"
                        value={Array.isArray(data.vc_keywords) ? data.vc_keywords : (data.vc_keywords ? String(data.vc_keywords).split(',').map(s => s.trim()).filter(Boolean) : [])}
                        placeholder="Type a keyword and press Enter"
                        onChange={(value) => {
                          const arr = [...multilingualData];
                          arr[index].vc_keywords = value;
                          setMultilingualData(arr);
                        }}
                        tokenSeparators={[',']}
                        open={false}
                      />
                    </Form.Item>
                  </div>
                </Card>
              ))}
            </div>
          </Card>

          <Form.Item style={{ marginTop: "32px" }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Add Category
              </Button>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                size="large"
              >
                Reset
              </Button>

              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Shows List - Only show when not an ad */}
      {isAd === "N" && (
        <Card style={{ marginTop: "24px" }}>
          <Spin spinning={showsSpinner}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Shows List
              </Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddShow}
              >
                Add Show
              </Button>
            </div>

            <Table
              columns={showsColumns}
              dataSource={showsList}
              rowKey={(record, index) => index}
              pagination={false}
              size="middle"
              locale={{ emptyText: "No shows added yet" }}
            />
          </Spin>
        </Card>
      )}

      {/* Add Show Modal */}
      <Modal
        title="Add Show"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={750}
      >
        <Form layout="vertical">
          <Form.Item label="Show Type">
            <Select
              placeholder="Please select show type"
              value={showForm.show_type}
              onChange={handleShowTypeChange}
            >
              <Option value="movie">Movie</Option>
              <Option value="series">Series</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Select Show">
            {showForm.show_type === "movie" && (
              <Select
                placeholder="Please select movie"
                value={showForm.show}
                onChange={handleShowChange}
              >
                {videoShows.map((show) => (
                  <Option key={show.video_show_id} value={JSON.stringify(show)}>
                    {show.video_show_title}
                  </Option>
                ))}
              </Select>
            )}

            {showForm.show_type === "series" && (
              <Select
                placeholder="Please select series"
                value={showForm.show}
                onChange={handleShowChange}
              >
                {tvShows.map((show) => (
                  <Option key={show.tv_show_id} value={JSON.stringify(show)}>
                    {show.tv_show_name}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          {showForm.show_type === "series" && (
            <Form.Item label="Select Season">
              <Select
                placeholder="Please select season"
                value={showForm.season}
                onChange={handleSeasonChange}
                allowClear
              >
                <Option value={null}>No Season</Option>
                {seasonsOfSeries.map((season) => (
                  <Option key={season.season_id} value={JSON.stringify(season)}>
                    {season.season_title}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginTop: "24px", textAlign: "right" }}>
            <Space>
              <Button onClick={() => setVisible(false)}>Cancel</Button>
              <Button type="primary" onClick={handleSubmitShow}>
                Add Show
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddCategory;
